import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';
import { 
  PanelTop, Square, DoorOpen, Layers, Sun, 
  GalleryHorizontalEnd, ShowerHead, Building2, Fence,
  ArrowRight
} from 'lucide-react';
import { useLanguage } from '../i18n/LanguageContext';
import { TiltCard } from './InteractiveEffects';

const Services = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const { language, isRTL } = useLanguage();

  const fontFamily = isRTL ? "'Noto Sans Arabic', sans-serif" : "'Outfit', sans-serif";

  // Service data with high-quality images
  const services = {
    en: [
      {
        icon: PanelTop,
        title: 'Aluminium Windows & Doors',
        description: 'High-quality aluminium windows and doors with thermal break technology. Sliding, casement, tilt & turn, and folding options available for residential and commercial projects.',
        image: 'https://images.unsplash.com/photo-1616843221271-a3447ca74df3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzd8MHwxfHNlYXJjaHwxfHxhbHVtaW5pdW0lMjB3aW5kb3clMjBtb2Rlcm4lMjBob3VzZSUyMGluc3RhbGxhdGlvbnxlbnwwfHx8fDE3NzY2OTA3MjJ8MA&ixlib=rb-4.1.0&q=85',
      },
      {
        icon: Square,
        title: 'Glass Partitions',
        description: 'Frameless and framed glass partitions for offices, homes, and commercial spaces. Create modern workspaces with acoustic options and privacy solutions.',
        image: 'https://images.unsplash.com/photo-1770048532712-4fde5ef7eb90?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzV8MHwxfHNlYXJjaHwyfHxnbGFzcyUyMHBhcnRpdGlvbiUyMG9mZmljZSUyMG1vZGVybnxlbnwwfHx8fDE3NzY2OTA3MjR8MA&ixlib=rb-4.1.0&q=85',
      },
      {
        icon: DoorOpen,
        title: 'Bi-Folding & Sliding Doors',
        description: 'Space-efficient bi-folding and slim sliding door systems that create seamless indoor-outdoor transitions. Maximize natural light with large glass panels.',
        image: 'https://images.unsplash.com/photo-1758998202918-d921125a700f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzV8MHwxfHNlYXJjaHwxfHxiaSUyMGZvbGRpbmclMjBnbGFzcyUyMGRvb3JzJTIwcGF0aW98ZW58MHx8fHwxNzc2NjkwNzI1fDA&ixlib=rb-4.1.0&q=85',
      },
      {
        icon: ShowerHead,
        title: 'Shower Enclosures',
        description: 'Modern frameless glass shower partitions and enclosures with premium hardware. Elegant designs that enhance bathroom aesthetics while containing water effectively.',
        image: 'https://images.unsplash.com/photo-1771929662486-f793e08f0f16?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjd8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBzaG93ZXIlMjBnbGFzcyUyMGVuY2xvc3VyZSUyMGJhdGhyb29tfGVufDB8fHx8MTc3NjY5MDcyNnww&ixlib=rb-4.1.0&q=85',
      },
      {
        icon: Fence,
        title: 'Glass Railings & Balustrades',
        description: 'Elegant glass railings for staircases, balconies, and terraces. Enhance safety while maintaining unobstructed views with stainless steel or aluminium frames.',
        image: 'https://images.pexels.com/photos/35699361/pexels-photo-35699361.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
      },
      {
        icon: Building2,
        title: 'Curtain Walls & Facades',
        description: 'Structural glazing and curtain wall systems for commercial buildings. Energy-efficient designs that create a sleek, modern aesthetic for your building exterior.',
        image: 'https://images.pexels.com/photos/27985107/pexels-photo-27985107.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
      },
      {
        icon: Sun,
        title: 'Skylights',
        description: 'Fixed and operable skylights that bring abundant natural light into your spaces. Energy-efficient designs that reduce lighting costs while adding architectural appeal.',
        image: 'https://images.pexels.com/photos/11844536/pexels-photo-11844536.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
      },
      {
        icon: GalleryHorizontalEnd,
        title: 'Mirrors & Decorative Glass',
        description: 'Custom designed mirrors, tinted glass, frosted glass, and decorative glass solutions. Transform interiors with artistic aesthetics and enhanced depth.',
        image: 'https://images.pexels.com/photos/8134818/pexels-photo-8134818.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
      },
      {
        icon: Layers,
        title: 'ACP Cladding',
        description: 'Aluminium Composite Panel cladding for building exteriors. Lightweight, durable, and available in various colors with excellent thermal insulation properties.',
        image: 'https://images.pexels.com/photos/10958536/pexels-photo-10958536.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
      },
    ],
    ar: [
      {
        icon: PanelTop,
        title: 'نوافذ وأبواب الألمنيوم',
        description: 'نوافذ وأبواب ألمنيوم عالية الجودة مع تقنية الفاصل الحراري. خيارات منزلقة وجانبية وقابلة للإمالة والدوران والطي متاحة للمشاريع السكنية والتجارية.',
        image: 'https://images.unsplash.com/photo-1616843221271-a3447ca74df3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzd8MHwxfHNlYXJjaHwxfHxhbHVtaW5pdW0lMjB3aW5kb3clMjBtb2Rlcm4lMjBob3VzZSUyMGluc3RhbGxhdGlvbnxlbnwwfHx8fDE3NzY2OTA3MjJ8MA&ixlib=rb-4.1.0&q=85',
      },
      {
        icon: Square,
        title: 'فواصل زجاجية',
        description: 'فواصل زجاجية بدون إطار وبإطار للمكاتب والمنازل والمساحات التجارية. أنشئ مساحات عمل حديثة مع خيارات عازلة للصوت وحلول الخصوصية.',
        image: 'https://images.unsplash.com/photo-1770048532712-4fde5ef7eb90?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzV8MHwxfHNlYXJjaHwyfHxnbGFzcyUyMHBhcnRpdGlvbiUyMG9mZmljZSUyMG1vZGVybnxlbnwwfHx8fDE3NzY2OTA3MjR8MA&ixlib=rb-4.1.0&q=85',
      },
      {
        icon: DoorOpen,
        title: 'أبواب قابلة للطي ومنزلقة',
        description: 'أنظمة أبواب قابلة للطي ومنزلقة رفيعة توفر المساحة وتخلق انتقالات سلسة بين الداخل والخارج. تعظيم الإضاءة الطبيعية مع الألواح الزجاجية الكبيرة.',
        image: 'https://images.unsplash.com/photo-1758998202918-d921125a700f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzV8MHwxfHNlYXJjaHwxfHxiaSUyMGZvbGRpbmclMjBnbGFzcyUyMGRvb3JzJTIwcGF0aW98ZW58MHx8fHwxNzc2NjkwNzI1fDA&ixlib=rb-4.1.0&q=85',
      },
      {
        icon: ShowerHead,
        title: 'كبائن الدش',
        description: 'فواصل وكبائن دش زجاجية بدون إطار حديثة مع أجهزة فاخرة. تصاميم أنيقة تعزز جمالية الحمام مع احتواء الماء بشكل فعال.',
        image: 'https://images.unsplash.com/photo-1771929662486-f793e08f0f16?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjd8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBzaG93ZXIlMjBnbGFzcyUyMGVuY2xvc3VyZSUyMGJhdGhyb29tfGVufDB8fHx8MTc3NjY5MDcyNnww&ixlib=rb-4.1.0&q=85',
      },
      {
        icon: Fence,
        title: 'درابزين وحواجز زجاجية',
        description: 'درابزين زجاجي أنيق للسلالم والشرفات والتراسات. عزز السلامة مع الحفاظ على إطلالات غير معرقلة مع إطارات من الفولاذ المقاوم للصدأ أو الألمنيوم.',
        image: 'https://images.pexels.com/photos/35699361/pexels-photo-35699361.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
      },
      {
        icon: Building2,
        title: 'الجدران الستائرية والواجهات',
        description: 'أنظمة التزجيج الهيكلي والجدران الستائرية للمباني التجارية. تصاميم موفرة للطاقة تخلق مظهراً عصرياً أنيقاً لواجهة المبنى.',
        image: 'https://images.pexels.com/photos/27985107/pexels-photo-27985107.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
      },
      {
        icon: Sun,
        title: 'فتحات السقف',
        description: 'فتحات سقف ثابتة وقابلة للفتح تجلب الإضاءة الطبيعية الوفيرة إلى مساحاتك. تصاميم موفرة للطاقة تقلل تكاليف الإضاءة مع إضافة جاذبية معمارية.',
        image: 'https://images.pexels.com/photos/11844536/pexels-photo-11844536.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
      },
      {
        icon: GalleryHorizontalEnd,
        title: 'المرايا والزجاج الزخرفي',
        description: 'مرايا مصممة حسب الطلب وزجاج ملون وزجاج مصنفر وحلول زجاج زخرفي. حوّل الديكورات الداخلية بجماليات فنية وعمق معزز.',
        image: 'https://images.pexels.com/photos/8134818/pexels-photo-8134818.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
      },
      {
        icon: Layers,
        title: 'كسوة الألواح المركبة',
        description: 'كسوة ألواح الألمنيوم المركبة لواجهات المباني الخارجية. خفيفة الوزن ومتينة ومتوفرة بألوان متنوعة مع خصائص عزل حراري ممتازة.',
        image: 'https://images.pexels.com/photos/10958536/pexels-photo-10958536.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
      },
    ],
  };

  const t = {
    badge: language === 'ar' ? 'ما نقدمه' : 'What We Offer',
    title: language === 'ar' ? 'خدماتنا' : 'Our Services',
    subtitle: language === 'ar' 
      ? 'حلول الألمنيوم والزجاج الفاخرة للمشاريع السكنية والتجارية في جميع أنحاء الإمارات.'
      : 'Premium aluminium and glass solutions for residential and commercial projects across the UAE.',
    learnMore: language === 'ar' ? 'اطلب عرض سعر' : 'Get Quote',
  };

  const currentServices = services[language] || services.en;

  return (
    <section 
      id="services" 
      className="py-24 md:py-32 px-6 relative"
      data-testid="services-section"
      ref={ref}
    >
      {/* Glassy background overlay */}
      <div className="absolute inset-0 bg-gradient-to-b from-white/40 via-white/60 to-white/40 backdrop-blur-sm"></div>
      
      <div className="max-w-7xl mx-auto relative z-10">
        {/* Section Header */}
        <motion.div 
          className={`mb-16 text-center ${isRTL ? 'text-right' : ''}`}
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
        >
          <motion.span 
            className="text-xs tracking-[0.2em] uppercase font-semibold text-[#25D366] block mb-4"
            initial={{ opacity: 0 }}
            animate={isInView ? { opacity: 1 } : {}}
            transition={{ duration: 0.5, delay: 0.2 }}
            style={{ fontFamily }}
          >
            {t.badge}
          </motion.span>
          <motion.h2 
            className="text-3xl sm:text-4xl md:text-5xl font-medium tracking-tight text-slate-800 mb-6"
            style={{ fontFamily }}
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
            {t.title}
          </motion.h2>
          <motion.p 
            className="text-slate-600 max-w-2xl mx-auto text-lg"
            style={{ fontFamily: isRTL ? "'Noto Sans Arabic', sans-serif" : "'Manrope', sans-serif" }}
            initial={{ opacity: 0 }}
            animate={isInView ? { opacity: 1 } : {}}
            transition={{ duration: 0.5, delay: 0.4 }}
          >
            {t.subtitle}
          </motion.p>
        </motion.div>

        {/* Services List with Images */}
        <div className="space-y-8">
          {currentServices.map((service, index) => (
            <TiltCard key={index} intensity={5}>
              <motion.div
                className={`glass-card rounded-3xl overflow-hidden group cursor-pointer ${isRTL ? 'flex-row-reverse' : ''}`}
                data-testid={`service-card-${index}`}
                initial={{ opacity: 0, y: 40 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: 0.1 * index }}
              >
                <div className={`flex flex-col lg:flex-row ${isRTL ? 'lg:flex-row-reverse' : ''} ${index % 2 === 1 ? 'lg:flex-row-reverse' : ''}`}>
                  {/* Image Side */}
                  <div className="lg:w-1/2 relative overflow-hidden">
                    <motion.div 
                      className="h-64 lg:h-80 w-full"
                      whileHover={{ scale: 1.05 }}
                      transition={{ duration: 0.6 }}
                    >
                      <img
                        src={service.image}
                        alt={service.title}
                        className="w-full h-full object-cover"
                      />
                      {/* Overlay gradient */}
                      <div className="absolute inset-0 bg-gradient-to-t from-slate-900/40 via-transparent to-transparent lg:bg-gradient-to-r lg:from-transparent lg:via-transparent lg:to-white/20"></div>
                    </motion.div>
                    
                    {/* Category badge on image */}
                    <div className={`absolute top-4 ${isRTL ? 'right-4' : 'left-4'}`}>
                      <span className="px-3 py-1.5 bg-white/90 backdrop-blur-sm text-[#25D366] text-xs font-semibold rounded-full uppercase tracking-wider">
                        {language === 'ar' ? 'الألمنيوم والزجاج' : 'Aluminium & Glass'}
                      </span>
                    </div>
                  </div>

                  {/* Content Side */}
                  <div className={`lg:w-1/2 p-8 lg:p-10 flex flex-col justify-center ${isRTL ? 'text-right' : ''}`}>
                    <div className={`flex items-center gap-4 mb-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
                      <motion.div 
                        className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#25D366]/20 to-emerald-400/10 border border-[#25D366]/20 flex items-center justify-center group-hover:from-[#25D366]/30 group-hover:to-emerald-400/20 transition-all duration-300"
                        whileHover={{ rotate: 360, scale: 1.1 }}
                        transition={{ duration: 0.6 }}
                      >
                        <service.icon size={28} className="text-[#25D366]" />
                      </motion.div>
                      <div className={`h-px flex-1 bg-gradient-to-r ${isRTL ? 'from-transparent to-slate-200' : 'from-slate-200 to-transparent'}`}></div>
                    </div>
                    
                    <h3 
                      className="text-2xl lg:text-3xl font-medium text-slate-800 mb-4 group-hover:text-[#25D366] transition-colors"
                      style={{ fontFamily }}
                    >
                      {service.title}
                    </h3>
                    
                    <p 
                      className="text-slate-600 leading-relaxed mb-6 text-base lg:text-lg"
                      style={{ fontFamily: isRTL ? "'Noto Sans Arabic', sans-serif" : "'Manrope', sans-serif" }}
                    >
                      {service.description}
                    </p>

                    {/* CTA Button */}
                    <motion.a
                      href={`https://wa.me/971566832840?text=${encodeURIComponent(`Hello, I'm interested in ${service.title}`)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`inline-flex items-center gap-2 text-[#25D366] font-medium group/btn ${isRTL ? 'flex-row-reverse' : ''}`}
                      whileHover={{ x: isRTL ? -5 : 5 }}
                    >
                      <span>{t.learnMore}</span>
                      <motion.div
                        animate={{ x: [0, 5, 0] }}
                        transition={{ duration: 1.5, repeat: Infinity }}
                      >
                        <ArrowRight size={18} className={isRTL ? 'rotate-180' : ''} />
                      </motion.div>
                    </motion.a>
                  </div>
                </div>
              </motion.div>
            </TiltCard>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Services;
